import { StringFormatter } from '@repo/toolkit/string'
import Checkbox from '@repo/ui/checkbox'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getHobbies } from '../../../../api/hobbies/get-hobbies'
import { HobbyAudienceEnum } from '../../../../lib/types/enums/hobby/hobby-audience.enum'
import {
  LanguageEnum,
  languageEnumMapping
} from '../../../../lib/types/enums/languages/language.enum'
import {
  LanguageLevelEnum,
  languageLevelEnumMapping
} from '../../../../lib/types/enums/languages/language-level.enum'
import {
  EvalutationEnum,
  evalutationEnumMapping
} from '../../../../lib/types/enums/staff-application/evalutation.enum'
import { LanguageLevelInput } from '../../../../lib/types/models/languages/language-level-input'
import { ScoreCardInput } from '../../../../lib/types/models/score-card/score-card-input'
import { RadioLettersOptions } from '../../../../lib/types/utils'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'

const RadioLanguage = ({
  label,
  value: selectedValue,
  icon: Icon,
  ...props
}: RadioLettersOptions<LanguageLevelEnum>) => {
  return (
    <div className={'flex flex-row items-center justify-between'}>
      <Typography
        variant={'label-semibold'}
        className={'text-grey-lighter flex gap-1 items-center'}
      >
        {label}
        {Icon && <Icon className={'w-3 h-3 rounded-full'} />}
      </Typography>

      <div className={'flex flex-row items-center gap-7'}>
        {Object.values(LanguageLevelEnum).map((value) => (
          <RadioInput
            {...props}
            key={value}
            label={languageLevelEnumMapping[value].label}
            checked={selectedValue === value}
            onCheck={() => props.onChange(value)}
          />
        ))}
      </div>
    </div>
  )
}

const RadioLetters = ({
  label,
  value: selectedValue,
  ...props
}: RadioLettersOptions<EvalutationEnum>) => {
  return (
    <div className={'flex flex-row items-center justify-between'}>
      <Typography variant={'label-semibold'} className={'text-grey-lighter'}>
        {label}
      </Typography>

      <div className={'flex flex-row items-center gap-7'}>
        {Object.values(EvalutationEnum)
          .sort((a, b) =>
            evalutationEnumMapping[a].letter.localeCompare(evalutationEnumMapping[b].letter)
          )
          .map((value) => (
            <RadioInput
              {...props}
              key={value}
              label={evalutationEnumMapping[value].letter}
              checked={selectedValue === value}
              onCheck={() => props.onChange(value)}
            />
          ))}
      </div>
    </div>
  )
}

interface EvaluationProps {
  disabled?: boolean
}

export default function PcrScoreCardFormEvaluation({ disabled }: EvaluationProps) {
  const form = useFormContext<ScoreCardInput>()
  const languageMapping = languageEnumMapping()

  const { data: hobbiesData, isPending: hobbiesPending } = useQuery({
    queryKey: [
      'get-hobbies',
      { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }
    ],
    queryFn: () =>
      getHobbies({
        filter: { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }
      })
  })

  const hobbies = form.watch('hobbies')

  return (
    <div className={'flex flex-col gap-5'}>
      {/* summary evaluation */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Résumé de l&apos;évaluation</Typography>
        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'presentation'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Présentation'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'ponctuality'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Ponctualité'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'motivation'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Motivation'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'senseOfResponsability'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Sens de responsabilité'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'relevanceOfAnswer'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters
                    {...field}
                    label={'Pertinence de la réponse à la mise en situation'}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'elocution'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Élocution'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'reactivity'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Réactivité'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'globalAppreciation'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <RadioLetters {...field} label={'Appréciation globale'} disabled={disabled} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Languages */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Niveau de langues</Typography>
        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'levelOfLanguages'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <>
                    {Object.values(LanguageEnum).map((language) => (
                      <RadioLanguage
                        key={language}
                        label={languageMapping[language].label}
                        icon={languageMapping[language].icon}
                        value={field.value?.find((lang) => lang.language === language)?.level}
                        onChange={(value) => {
                          const newLanguageLevel: LanguageLevelInput = {
                            language,
                            level: value
                          }

                          // Check if the language is already in the list
                          const index = field.value.findIndex((lang) => lang.language === language)

                          // If the language is already in the list, update it
                          if (index !== -1) {
                            const newLanguages = [...field.value]
                            newLanguages[index] = newLanguageLevel
                            field.onChange(newLanguages)
                          } else {
                            field.onChange([...field.value, newLanguageLevel])
                          }
                        }}
                        disabled={disabled}
                      />
                    ))}
                  </>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* hobbies */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Talent Particulier</Typography>

        <div>
          <FormField
            control={form.control}
            name={'hobbies'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  {!hobbiesData ? (
                    <Typography
                      variant={'secondary-body'}
                      className={'text-center text-grey-light-active'}
                    >
                      {hobbiesPending ? 'Chargement ...' : 'Aucune donnée à afficher'}
                    </Typography>
                  ) : (
                    <div className={'flex flex-col gap-1'}>
                      {hobbiesData.data.map((hobby) => (
                        <Checkbox
                          key={hobby.id}
                          label={StringFormatter.toTitleCase(hobby.name)}
                          checked={hobbies ? hobbies.includes(hobby.id) : undefined}
                          onChange={(value) => {
                            if (value)
                              field.onChange([...(field.value ? field.value : []), hobby.id])
                            else field.onChange([...field.value.filter((id) => id !== hobby.id)])
                          }}
                          disabled={disabled}
                        />
                      ))}
                    </div>
                  )}
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'doesHaveAPeculiarTalent'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    type={'text'}
                    placeholder={'Autre ? Écrivez ici'}
                    onChange={field.onChange}
                    value={field.value}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* does he knows a LEC person */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>
          Le candidat connait-il quelqu'un qui travaille pour LEC?
        </Typography>
        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'doesKnowLECPerson'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    type={'text'}
                    placeholder={'Si oui, précisez..'}
                    onChange={field.onChange}
                    value={field.value}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* comments */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Commentaire sur l'entretien</Typography>
        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'comments'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    type={'textarea'}
                    placeholder={'Écrivez ici..'}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* recrute/ is recommended to hire */}
      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>A recruter?</Typography>
        <div className={'flex flex-col gap-2'}>
          <FormField
            control={form.control}
            name={'isApplicantRecommendedForHire'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <div className={'flex flex-row items-center gap-7'}>
                    <RadioInput
                      {...field}
                      label={'Oui'}
                      checked={field.value === true}
                      name={'true-radio-input-Oui'}
                      onCheck={() => field.onChange(true)}
                      disabled={disabled}
                    />
                    <RadioInput
                      {...field}
                      label={'Non'}
                      checked={field.value === false}
                      name={'fale-radio-input-Non'}
                      onCheck={() => field.onChange(false)}
                      disabled={disabled}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  )
}
